/**
 * One XHR request with upload progress.
 */

/**
 * Send the request. Resolves with the finished request for any HTTP status, so the caller reads the response.
 * Rejects with an Error whose `kind` is `network`, `aborted` or `timeout`.
 *
 * @param {{
 *   method: string,
 *   url: string,
 *   headers?: Object<string, string>,
 *   body?: Document|XMLHttpRequestBodyInit|null,
 *   signal?: AbortSignal,
 *   onProgress?: function(number): void,
 *   stallTimeoutMs?: number,
 * }} request onProgress gets the sent fraction, from 0 to 1.
 *   stallTimeoutMs aborts the request when no bytes move in either direction for that long.
 * @returns {Promise<XMLHttpRequest>}
 */
export function sendXhr({ method, url, headers = {}, body = null, signal, onProgress, stallTimeoutMs }) {
    return new Promise((resolve, reject) => {
        const failure = (kind) => Object.assign(new Error(kind), { kind });
        if (signal?.aborted) {
            reject(failure('aborted'));
            return;
        }
        const xhr = new XMLHttpRequest();
        const onAbort = () => xhr.abort();
        signal?.addEventListener('abort', onAbort);
        let stallTimer = null;
        let stalled = false;
        const armStallTimer = () => {
            if (!stallTimeoutMs) return;
            clearTimeout(stallTimer);
            stallTimer = setTimeout(() => {
                stalled = true;
                xhr.abort();
            }, stallTimeoutMs);
        };
        const settle = (callback) => {
            if (stallTimer !== null) clearTimeout(stallTimer);
            signal?.removeEventListener('abort', onAbort);
            callback();
        };

        xhr.upload.addEventListener('progress', (event) => {
            armStallTimer();
            if (event.lengthComputable && onProgress) {
                onProgress(event.loaded / event.total);
            }
        });
        xhr.onprogress = armStallTimer;
        xhr.onload = () => settle(() => resolve(xhr));
        xhr.onerror = () => settle(() => reject(failure('network')));
        xhr.onabort = () => settle(() => reject(failure(stalled ? 'timeout' : 'aborted')));

        try {
            xhr.open(method, url, true);
            for (const [name, value] of Object.entries(headers)) {
                xhr.setRequestHeader(name, value);
            }
            xhr.send(body);
            armStallTimer();
        } catch {
            settle(() => reject(failure('network')));
        }
    });
}
